import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, QrCode, Crosshair, X } from 'lucide-react';

const KINDS = {
  flyer: { label: 'Flyer (Blanko-Vordrucke)', dxKey: 'qr_overlay_dx_mm', dyKey: 'qr_overlay_dy_mm', test: api.downloadQrOverlayTestPdf,
    testName: 'OLTA_QR_Kalibrierung.pdf', paper: 'auf Blanko-Flyer drucken (QR-Seite), einseitig' },
  breakup: { label: 'Break-up-Briefpapier', dxKey: 'bl_qr_dx_mm', dyKey: 'bl_qr_dy_mm', test: api.downloadBreakupQrTestPdf,
    testName: 'OLTA_Breakup_QR_Kalibrierung.pdf', paper: 'auf Break-up-Briefpapier drucken (Feld unten rechts)' },
};

const blobError = async (err) => {
  let detail = err.response?.data?.detail;
  if (err.response?.data instanceof Blob) {
    try { detail = JSON.parse(await err.response.data.text())?.detail; } catch { detail = ''; }
  }
  return detail || 'Download fehlgeschlagen';
};

const saveAs = (data, filename, type = 'application/pdf') => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function KindBlock({ kind, letters, settings, onRemove }) {
  const cfg = KINDS[kind];
  const [dx, setDx] = useState('0');
  const [dy, setDy] = useState('0');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (!settings) return;
    setDx(String(settings[cfg.dxKey] ?? 0));
    setDy(String(settings[cfg.dyKey] ?? 0));
  }, [settings, cfg.dxKey, cfg.dyKey]);

  const parse = () => {
    const sx = String(dx).trim().replace(',', '.');
    const sy = String(dy).trim().replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(sx) || !/^-?\d+(\.\d+)?$/.test(sy)) { toast.error('Versatz muss eine Zahl in mm sein'); return null; }
    return { x: parseFloat(sx), y: parseFloat(sy) };
  };
  const saveOffsets = async (o) => {
    try { await api.updatePrintSettings({ [cfg.dxKey]: o.x, [cfg.dyKey]: o.y }); return true; }
    catch (err) { toast.error(err.response?.data?.detail || 'Versatz speichern fehlgeschlagen'); return false; }
  };

  const testPage = async () => {
    const o = parse(); if (!o) return;
    setBusy('test');
    try {
      if (!(await saveOffsets(o))) return;
      const res = await cfg.test();
      saveAs(res.data, cfg.testName);
      toast.success('Testseite geladen — auf EIN Blatt drucken und Rahmen prüfen');
    } catch (err) { toast.error(await blobError(err)); }
    finally { setBusy(''); }
  };

  const download = async () => {
    const o = parse(); if (!o) return;
    setBusy('overlay');
    try {
      if (!(await saveOffsets(o))) return;
      const res = await api.downloadQrOverlaySelected(letters.map(l => l.id), kind, o.x, o.y);
      const n = res.headers?.['x-flyer-count'] || letters.length;
      const skipped = parseInt(res.headers?.['x-skipped-count'] || '0', 10);
      saveAs(res.data, `${kind === 'breakup' ? 'OLTA_Breakup_QR_Overlay_Nachdruck' : 'OLTA_QR_Overlay_Nachdruck'}_${n}x.pdf`);
      toast.success(`${n} QR-Seite${n === 1 || n === '1' ? '' : 'n'} geladen (alphabetisch) — ${cfg.paper}. Danach Flyer-Text/Brief über „Vorschau · Nachschreiben“ neu schreiben lassen.`, { duration: 10000 });
      if (skipped) toast.warning(`${skipped} übersprungen — ohne Landing Page kein QR möglich`, { duration: 8000 });
    } catch (err) { toast.error(await blobError(err), { duration: 10000 }); }
    finally { setBusy(''); }
  };

  return (
    <div className="border border-border rounded-sm p-2.5 space-y-2" data-testid={`qr-reprint-block-${kind}`}>
      <div className="flex items-center gap-2">
        <QrCode className="w-3.5 h-3.5 text-sky-400" />
        <span className="text-xs font-medium text-foreground">{cfg.label}</span>
        <span className="text-[10px] font-mono text-muted-foreground" data-testid={`qr-reprint-count-${kind}`}>{letters.length} Seite{letters.length !== 1 ? 'n' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto" data-testid={`qr-reprint-list-${kind}`}>
        {letters.map(l => (
          <span key={l.id} className="inline-flex items-center gap-1 text-[10px] border border-border rounded-sm px-1.5 py-0.5 text-foreground/90">
            {l.company_name}
            <button onClick={() => onRemove(l.id)} className="text-muted-foreground hover:text-red-400" title="Aus Auswahl entfernen"
              data-testid={`qr-reprint-remove-${l.id}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-muted-foreground">Versatz</span>
        <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
          X
          <input value={dx} onChange={e => setDx(e.target.value)} data-testid={`qr-reprint-dx-${kind}`}
            className="w-14 h-6 px-1.5 text-[11px] font-mono bg-secondary border border-border rounded-sm text-foreground" />
          mm
        </label>
        <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
          Y
          <input value={dy} onChange={e => setDy(e.target.value)} data-testid={`qr-reprint-dy-${kind}`}
            className="w-14 h-6 px-1.5 text-[11px] font-mono bg-secondary border border-border rounded-sm text-foreground" />
          mm
        </label>
        <span className="text-[10px] text-muted-foreground/70">positiv = nach rechts/unten · wird als Standard gespeichert</span>
        <span className="ml-auto flex items-center gap-2">
          <button onClick={testPage} disabled={busy !== ''} data-testid={`qr-reprint-test-${kind}`}
            className="flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 disabled:opacity-50 transition-colors">
            {busy === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />} Testseite
          </button>
          <Button size="sm" onClick={download} disabled={busy !== '' || !letters.length} data-testid={`qr-reprint-download-${kind}`}
            className="h-6 text-[10px] rounded-sm bg-sky-600 hover:bg-sky-500 text-white">
            {busy === 'overlay' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <QrCode className="w-3 h-3 mr-1" />}
            QR-Overlay laden ({letters.length})
          </Button>
        </span>
      </div>
    </div>
  );
}

export function QrReprintDialog({ open, onOpenChange, letters, onRemove }) {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.printSettings().then(r => setSettings(r.data)).catch(() => setSettings({}));
  }, [open]);
  const flyers = letters.filter(l => l.letter_kind !== 'breakup');
  const breakups = letters.filter(l => l.letter_kind === 'breakup');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="qr-reprint-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <QrCode className="w-4 h-4 text-sky-400" /> QR-Overlay neu drucken — {letters.length} ausgewählt
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Für falsch justierte Blätter: Versatz korrigieren, Testseite prüfen, dann nur die ausgewählten QR-Codes neu drucken —
            in alphabetischer Reihenfolge, unabhängig vom laufenden Serien-Stapel. Bereits geschriebene Briefe sind ausdrücklich erlaubt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {flyers.length > 0 && <KindBlock kind="flyer" letters={flyers} settings={settings} onRemove={onRemove} />}
          {breakups.length > 0 && <KindBlock kind="breakup" letters={breakups} settings={settings} onRemove={onRemove} />}
          {!letters.length && <p className="text-xs text-muted-foreground py-4">Keine Briefe ausgewählt.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default QrReprintDialog;
